// Cambodia Administrative Divisions API Service
// Integrates with local API endpoints for detailed Cambodia administrative data
import instance from '@/services/api.ts';

// Types for Cambodia administrative divisions
export interface Province {
  id: number;
  name: string;
  name_khmer: string;
  code: string;
  created_at?: string;
  updated_at?: string;
}

export interface District {
  id: number;
  name: string;
  name_khmer: string;
  province_id: number;
  type: 'district' | 'city' | 'khan'; // ស្រុក | ក្រុង | ខណ្ឌ
  created_at?: string;
  updated_at?: string;
  province?: Province;
}

export interface City extends District {
  type: 'city';
}

export interface Khan extends District {
  type: 'khan';
}

export interface Commune {
  id: number;
  name: string;
  name_khmer: string;
  district_id?: number;
  city_id?: number;
  khan_id?: number;
  type: 'commune' | 'sangkat'; // ឃុំ | សង្កាត់
  created_at?: string;
  updated_at?: string;
  district?: District;
  city?: City;
  khan?: Khan;
}

export interface Village {
  id: number;
  name: string;
  name_khmer: string;
  commune_id?: number;
  sangkat_id?: number;
  type: 'village'; // ភូមិ
  created_at?: string;
  updated_at?: string;
  commune?: Commune;
  sangkat?: Commune;
}

export interface SearchParams {
  q?: string;
  province_id?: number;
  district_id?: number;
  city_id?: number;
  khan_id?: number;
}

// Cache configuration
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const CACHE_VERSION = '1.1';

interface CachedData<T> {
  data: T;
  timestamp: number;
  version: string;
}

class CambodiaAdminApiService {
  private static instance: CambodiaAdminApiService;
  private cachePrefix = 'cambodia_admin_';

  public static getInstance(): CambodiaAdminApiService {
    if (!CambodiaAdminApiService.instance) {
      CambodiaAdminApiService.instance = new CambodiaAdminApiService();
    }
    return CambodiaAdminApiService.instance;
  }

  // Cache management
  private getCacheKey(endpoint: string, params?: any): string {
    const paramStr = params ? JSON.stringify(params) : '';
    return `${this.cachePrefix}${endpoint}_${btoa(paramStr)}`;
  }

  private isCacheValid(key: string): boolean {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return false;

      const parsedCache: CachedData<any> = JSON.parse(cached);
      const now = Date.now();

      return (
        parsedCache.version === CACHE_VERSION &&
        (now - parsedCache.timestamp) < CACHE_DURATION
      );
    } catch (error) {
      console.warn('Cache validation error:', error);
      return false;
    }
  }

  private getCachedData<T>(key: string): T | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const parsedCache: CachedData<T> = JSON.parse(cached);
      return parsedCache.data;
    } catch (error) {
      console.warn('Error reading cache:', error);
      return null;
    }
  }

  private setCachedData<T>(key: string, data: T): void {
    try {
      const cacheData: CachedData<T> = {
        data,
        timestamp: Date.now(),
        version: CACHE_VERSION
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Error setting cache:', error);
    }
  }

  // Province endpoints (ខេត្ត)
  async getProvinces(): Promise<Province[]> {
    const cacheKey = this.getCacheKey('provinces');

    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<Province[]>(cacheKey);
      if (cached) {
        console.log('Using cached provinces data');
        return cached;
      }
    }

    try {
      console.log('Fetching provinces from API...');
      const response = await instance.get('/cambodia/provinces');
      const provinces: Province[] = response.data.data;
      console.log(`Fetched ${provinces.length} provinces`);

      this.setCachedData(cacheKey, provinces);
      console.log(`Fetched and cached ${provinces.length} provinces`);
      return provinces;
    } catch (error) {
      console.error('Error fetching provinces:', error);
      // Return fallback provinces if API fails
      return this.getFallbackProvinces();
    }
  }

  async getProvince(id: number): Promise<Province> {
    const cacheKey = this.getCacheKey('province', { id });

    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<Province>(cacheKey);
      if (cached) return cached;
    }

    try {
      const response = await instance.get(`/cambodia/provinces/${id}`);
      const province: Province = response.data;

      this.setCachedData(cacheKey, province);
      return province;
    } catch (error) {
      console.error(`Error fetching province ${id}:`, error);
      throw error;
    }
  }

  async searchProvinces(searchTerm: string): Promise<Province[]> {
    try {
      const response = await instance.get('/cambodia/provinces/search', {
        params: { q: searchTerm }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching provinces:', error);
      throw error;
    }
  }

  // City endpoints (ក្រុង) - Urban Areas
  async getCities(provinceId: number): Promise<City[]> {
    const cacheKey = this.getCacheKey('cities', { provinceId });

    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<City[]>(cacheKey);
      if (cached) return cached;
    }

    try {
      const response = await instance.get(`/cambodia/provinces/${provinceId}/cities`);
      const cities: City[] = response.data.data || response.data;

      this.setCachedData(cacheKey, cities);
      return cities;
    } catch (error) {
      console.error(`Error fetching cities for province ${provinceId}:`, error);
      throw error;
    }
  }

  async getCity(id: number): Promise<City> {
    try {
      const response = await instance.get(`/cambodia/cities/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching city ${id}:`, error);
      throw error;
    }
  }

  // District endpoints (ស្រុក) - Rural Areas
  async getDistricts(provinceId: number): Promise<District[]> {
    const cacheKey = this.getCacheKey('districts', { provinceId });

    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<District[]>(cacheKey);
      if (cached) return cached;
    }

    try {
      const response = await instance.get(`/cambodia/provinces/${provinceId}/districts`);
      const districts: District[] = response.data.data || response.data;

      this.setCachedData(cacheKey, districts);
      return districts;
    } catch (error) {
      console.error(`Error fetching districts for province ${provinceId}:`, error);
      throw error;
    }
  }

  async getDistrict(id: number): Promise<District> {
    try {
      const response = await instance.get(`/cambodia/districts/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching district ${id}:`, error);
      throw error;
    }
  }

  async searchDistricts(searchTerm: string, provinceId?: number): Promise<District[]> {
    try {
      const params: SearchParams = { q: searchTerm };
      if (provinceId) params.province_id = provinceId;

      const response = await instance.get('/cambodia/districts/search', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching districts:', error);
      throw error;
    }
  }

  // Khan endpoints (ខណ្ឌ) - Only in Phnom Penh
  async getKhan(provinceId: number): Promise<Khan[]> {
    const cacheKey = this.getCacheKey('khan', { provinceId });

    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<Khan[]>(cacheKey);
      if (cached) return cached;
    }

    try {
      const response = await instance.get(`/cambodia/provinces/${provinceId}/khan`);
      const khan: Khan[] = response.data.data || response.data;

      this.setCachedData(cacheKey, khan);
      return khan;
    } catch (error) {
      console.error(`Error fetching khan for province ${provinceId}:`, error);
      // Return fallback Khan data for Phnom Penh if API fails
      if (provinceId === 1) {
        return this.getFallbackKhan();
      }
      return [];
    }
  }

  async getKhanById(id: number): Promise<Khan> {
    try {
      const response = await instance.get(`/cambodia/khan/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching khan ${id}:`, error);
      throw error;
    }
  }

  // Combined method to get cities, districts, and khan for a province
  async getCitiesDistrictsKhan(provinceId: number): Promise<{
    cities: City[];
    districts: District[];
    khan: Khan[];
  }> {
    try {
      const [cities, districts, khan] = await Promise.allSettled([
        this.getCities(provinceId),
        this.getDistricts(provinceId),
        this.getKhan(provinceId)
      ]);

      return {
        cities: cities.status === 'fulfilled' ? cities.value : [],
        districts: districts.status === 'fulfilled' ? districts.value : [],
        khan: khan.status === 'fulfilled' ? khan.value : []
      };
    } catch (error) {
      console.error(`Error fetching administrative divisions for province ${provinceId}:`, error);
      // Return empty arrays instead of throwing
      return {
        cities: [],
        districts: [],
        khan: []
      };
    }
  }

  // Commune endpoints (ឃុំ) - Rural Sub-districts
  async getCommunes(districtId: number): Promise<Commune[]> {
    const cacheKey = this.getCacheKey('communes', { districtId });

    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<Commune[]>(cacheKey);
      if (cached) return cached;
    }

    try {
      const response = await instance.get(`/cambodia/districts/${districtId}/communes`);
      const communes: Commune[] = response.data.data || response.data;

      this.setCachedData(cacheKey, communes);
      return communes;
    } catch (error) {
      console.error(`Error fetching communes for district ${districtId}:`, error);
      throw error;
    }
  }

  async getCommune(id: number): Promise<Commune> {
    try {
      const response = await instance.get(`/cambodia/communes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching commune ${id}:`, error);
      throw error;
    }
  }

  // Sangkat endpoints (សង្កាត់) - Urban Sub-districts
  async getSangkatsByCity(cityId: number): Promise<Commune[]> {
    const cacheKey = this.getCacheKey('sangkats_city', { cityId });

    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<Commune[]>(cacheKey);
      if (cached) return cached;
    }

    try {
      const response = await instance.get(`/cambodia/cities/${cityId}/sangkats`);
      const sangkats: Commune[] = response.data.data || response.data;

      this.setCachedData(cacheKey, sangkats);
      return sangkats;
    } catch (error) {
      console.error(`Error fetching sangkats for city ${cityId}:`, error);
      throw error;
    }
  }

  async getSangkatsByKhan(khanId: number): Promise<Commune[]> {
    const cacheKey = this.getCacheKey('sangkats_khan', { khanId });

    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<Commune[]>(cacheKey);
      if (cached) return cached;
    }

    try {
      const response = await instance.get(`/cambodia/khan/${khanId}/sangkats`);
      const sangkats: Commune[] = response.data.data || response.data;

      this.setCachedData(cacheKey, sangkats);
      return sangkats;
    } catch (error) {
      console.error(`Error fetching sangkats for khan ${khanId}:`, error);
      throw error;
    }
  }

  async getSangkat(id: number): Promise<Commune> {
    try {
      const response = await instance.get(`/cambodia/sangkats/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching sangkat ${id}:`, error);
      throw error;
    }
  }

  // Village endpoints (ភូមិ) - Rural and Urban Villages
  async getVillagesByCommune(communeId: number): Promise<Village[]> {
    const cacheKey = this.getCacheKey('villages_commune', { communeId });

    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<Village[]>(cacheKey);
      if (cached) return cached;
    }

    try {
      const response = await instance.get(`/cambodia/communes/${communeId}/villages`);
      const villages: Village[] = response.data.data || response.data;

      this.setCachedData(cacheKey, villages);
      return villages;
    } catch (error) {
      console.error(`Error fetching villages for commune ${communeId}:`, error);
      throw error;
    }
  }

  async getVillagesBySangkat(sangkatId: number): Promise<Village[]> {
    const cacheKey = this.getCacheKey('villages_sangkat', { sangkatId });

    if (this.isCacheValid(cacheKey)) {
      const cached = this.getCachedData<Village[]>(cacheKey);
      if (cached) return cached;
    }

    try {
      const response = await instance.get(`/cambodia/sangkats/${sangkatId}/villages`);
      const villages: Village[] = response.data.data || response.data;

      this.setCachedData(cacheKey, villages);
      return villages;
    } catch (error) {
      console.error(`Error fetching villages for sangkat ${sangkatId}:`, error);
      throw error;
    }
  }

  async getVillage(id: number): Promise<Village> {
    try {
      const response = await instance.get(`/cambodia/villages/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching village ${id}:`, error);
      throw error;
    }
  }

  async searchVillages(searchTerm: string, provinceId?: number): Promise<Village[]> {
    try {
      const params: SearchParams = { q: searchTerm };
      if (provinceId) params.province_id = provinceId;

      const response = await instance.get('/cambodia/villages/search', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching villages:', error);
      throw error;
    }
  }

  // Utility methods
  async getCompleteHierarchy(provinceId: number): Promise<{
    province: Province;
    administrativeDivisions: (City | District | Khan)[];
    subDivisions: { [key: number]: Commune[] };
    villages: { [key: number]: Village[] };
  }> {
    try {
      const [province, { cities, districts, khan }] = await Promise.all([
        this.getProvince(provinceId),
        this.getCitiesDistrictsKhan(provinceId)
      ]);

      const administrativeDivisions = [...cities, ...districts, ...khan];
      const subDivisions: { [key: number]: Commune[] } = {};
      const villages: { [key: number]: Village[] } = {};

      // Fetch communes/sangkats for each administrative division
      for (const division of administrativeDivisions) {
        if (division.type === 'district') {
          subDivisions[division.id] = await this.getCommunes(division.id);
        } else if (division.type === 'city') {
          subDivisions[division.id] = await this.getSangkatsByCity(division.id);
        } else if (division.type === 'khan') {
          subDivisions[division.id] = await this.getSangkatsByKhan(division.id);
        }

        // Fetch villages for each subdivision
        for (const subDiv of subDivisions[division.id] || []) {
          if (subDiv.type === 'commune') {
            villages[subDiv.id] = await this.getVillagesByCommune(subDiv.id);
          } else if (subDiv.type === 'sangkat') {
            villages[subDiv.id] = await this.getVillagesBySangkat(subDiv.id);
          }
        }
      }

      return {
        province,
        administrativeDivisions,
        subDivisions,
        villages
      };
    } catch (error) {
      console.error(`Error fetching complete hierarchy for province ${provinceId}:`, error);
      throw error;
    }
  }

  // Clear cache
  clearCache(): void {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.cachePrefix)) {
          localStorage.removeItem(key);
        }
      }
      console.log('Cambodia administrative cache cleared');
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  // Preload data for performance
  async preloadData(): Promise<void> {
    try {
      console.log('Preloading Cambodia administrative data...');

      // Load all provinces
      const provinces = await this.getProvinces();

      // Load administrative divisions for major provinces
      const majorProvinces = provinces.filter(p =>
        ['Phnom Penh', 'Siem Reap', 'Battambang', 'Kandal'].includes(p.name)
      );

      for (const province of majorProvinces) {
        await this.getCitiesDistrictsKhan(province.id);
      }

      console.log('Cambodia administrative data preloading completed');
    } catch (error) {
      console.error('Error preloading Cambodia administrative data:', error);
    }
  }

  // Public method to get a lookup map of fallback province Khmer names
  getFallbackProvinceLookup(): { byName: Record<string, string>; byCode: Record<string, string> } {
    const fallback = this.getFallbackProvinces();
    const byName: Record<string, string> = {};
    const byCode: Record<string, string> = {};
    for (const p of fallback) {
      byName[p.name] = p.name_khmer;
      byCode[p.code] = p.name_khmer;
    }
    return { byName, byCode };
  }

  // Fallback data methods
  private getFallbackProvinces(): Province[] {
    return [
      { id: 1, name: 'Phnom Penh', name_khmer: 'ភ្នំពេញ', code: 'PP' },
      { id: 2, name: 'Siem Reap', name_khmer: 'សៀមរាប', code: 'SR' },
      { id: 3, name: 'Battambang', name_khmer: 'បាត់ដំបង', code: 'BTB' },
      { id: 4, name: 'Kandal', name_khmer: 'កណ្ដាល', code: 'KDL' },
      { id: 5, name: 'Kampong Cham', name_khmer: 'កំពង់ចាម', code: 'KPC' },
      { id: 6, name: 'Kampong Thom', name_khmer: 'កំពង់ធំ', code: 'KPT' },
      { id: 7, name: 'Kampong Speu', name_khmer: 'កំពង់ស្ពឺ', code: 'KPS' },
      { id: 8, name: 'Kampong Chhnang', name_khmer: 'កំពង់ឆ្នាំង', code: 'KPN' },
      { id: 9, name: 'Prey Veng', name_khmer: 'ព្រៃវែង', code: 'PV' },
      { id: 10, name: 'Svay Rieng', name_khmer: 'ស្វាយរៀង', code: 'SVR' },
      { id: 11, name: 'Takeo', name_khmer: 'តាកែវ', code: 'TK' },
      { id: 12, name: 'Kampot', name_khmer: 'កំពត', code: 'KMP' },
      { id: 13, name: 'Preah Sihanouk', name_khmer: 'ព្រះសីហនុ', code: 'PS' },
      { id: 14, name: 'Koh Kong', name_khmer: 'កោះកុង', code: 'KK' },
      { id: 15, name: 'Pursat', name_khmer: 'ពោធិ៍សាត់', code: 'PST' },
      { id: 16, name: 'Banteay Meanchey', name_khmer: 'បន្ទាយមានជ័យ', code: 'BMC' },
      { id: 17, name: 'Oddar Meanchey', name_khmer: 'ឧត្តរមានជ័យ', code: 'OMC' },
      { id: 18, name: 'Pailin', name_khmer: 'ប៉ៃលិន', code: 'PLN' },
      { id: 19, name: 'Preah Vihear', name_khmer: 'ព្រះវិហារ', code: 'PVH' },
      { id: 20, name: 'Stung Treng', name_khmer: 'ស្ទឹងត្រែង', code: 'STG' },
      { id: 21, name: 'Ratanakiri', name_khmer: 'រតនៈគិរី', code: 'RTK' },
      { id: 22, name: 'Mondulkiri', name_khmer: 'មណ្ឌលគិរី', code: 'MDK' },
      { id: 23, name: 'Kratie', name_khmer: 'ក្រចេះ', code: 'KRT' },
      { id: 24, name: 'Kep', name_khmer: 'កែប', code: 'KEP' },
      { id: 25, name: 'Tboung Khmum', name_khmer: 'ត្បូងឃ្មុំ', code: 'TBK' }
    ];
  }

  private getFallbackKhan(): Khan[] {
    return [
      { id: 1, name: 'Chamkar Mon', name_khmer: 'ចំការមន', province_id: 1, type: 'khan' },
      { id: 2, name: 'Doun Penh', name_khmer: 'ដូនពេញ', province_id: 1, type: 'khan' },
      { id: 3, name: '7 Makara', name_khmer: '៧មករា', province_id: 1, type: 'khan' },
      { id: 4, name: 'Toul Kork', name_khmer: 'ទួលគោក', province_id: 1, type: 'khan' },
      { id: 5, name: 'Dangkao', name_khmer: 'ដង្កោ', province_id: 1, type: 'khan' },
      { id: 6, name: 'Mean Chey', name_khmer: 'មានជ័យ', province_id: 1, type: 'khan' },
      { id: 7, name: 'Russey Keo', name_khmer: 'ឬស្សីកែវ', province_id: 1, type: 'khan' },
      { id: 8, name: 'Sen Sok', name_khmer: 'សែនសុខ', province_id: 1, type: 'khan' },
      { id: 9, name: 'Por Sen Chey', name_khmer: 'បរសេនជ័យ', province_id: 1, type: 'khan' },
      { id: 10, name: 'Chroy Changvar', name_khmer: 'ជ្រោយចង្វា', province_id: 1, type: 'khan' },
      { id: 11, name: 'Prek Pnov', name_khmer: 'ព្រែកព្នៅ', province_id: 1, type: 'khan' },
      { id: 12, name: 'Chbar Ampov', name_khmer: 'ច្បារអំពៅ', province_id: 1, type: 'khan' },
      { id: 13, name: 'Kamboul', name_khmer: 'កំបូល', province_id: 1, type: 'khan' },
      { id: 14, name: 'Boeng Keng Kang', name_khmer: 'បឹងកេងកង', province_id: 1, type: 'khan' }
    ];
  }
}

// Export singleton instance
export const cambodiaAdminApi = CambodiaAdminApiService.getInstance();
